"use client"

import { useState, useEffect, useRef } from "react"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { ExternalLink, X, Sparkles, Crown, Gift, Percent } from "@/lib/icons"
import { cn } from "@/lib/utils"
import { useAdStore, trackImpression, trackClick } from "@/lib/ad-store"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useHasConsent } from "@/components/cookie-consent"
import { isAdCooldownExpired, isAdTestModeOn, markAdShown } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"

export interface InlineAd {
  id: string
  campaign_id: string
  type: "external" | "internal" | "promo"
  title: string
  subtitle?: string
  image_url: string
  media_url?: string
  media_type?: "image" | "gif" | "video"
  click_url: string
  cta_text?: string
  badge_text?: string
  badge_color?: string
  background_gradient?: string
  promo_code?: string
  discount_text?: string
  hide_badge?: boolean
  hide_dim?: boolean
  /** True for a membership/upgrade upsell ad (house_ads.is_upgrade_promo) —
   * the ONLY category suppressed for an ad-free viewer (real premium member
   * or an active Skip-Ads grant). Every other house ad and every creator
   * promo shows to ad-free viewers same as anyone else — see the render
   * gate below and creator_script_promos'/commission promos' own injection
   * sites in booru-grid.tsx, which never set this at all. */
  is_upgrade_promo?: boolean
  /** True for a third-party affiliate advertisement (house_ads.is_affiliate)
   * — served only via /api/ads/serve?affiliate=1 into its own rotation slot,
   * see components/booru-grid.tsx. Purely informational on this component;
   * it doesn't change the render gate. */
  is_affiliate?: boolean
}

interface InlineGridAdProps {
  ad: InlineAd
  aspectRatio?: "square" | "portrait" | "landscape"
  /** Fitted grid: keep the box at `aspectRatio` (creative shown whole inside it) instead of resizing to the media. */
  fixedShape?: boolean
  className?: string
  onDismiss?: () => void
  showDismiss?: boolean
}

export function InlineGridAd({
  ad,
  aspectRatio = "portrait",
  fixedShape = false,
  className,
  onDismiss,
  showDismiss = false,
}: InlineGridAdProps) {
  const router = useRouter()
  const hasConsent = useHasConsent()
  const adRef = useRef<HTMLDivElement>(null)
  const { sessionId, isPremiumUser } = useAdStore()
  // Same "Force Ads" test-mode override as booru-grid.tsx's
  // effectivePremiumUser — without it, a premium account could never see
  // this component render at all even after an ad is successfully injected
  // upstream (the injection isn't the only gate; this early-return is a
  // second, independent one).
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()
  const [isHovered, setIsHovered] = useState(false)
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false)
  const [canShow] = useState(() =>
    isAdCooldownExpired("inline_grid", useAdFrequencyStore.getState().getCooldownMinutes("inline_sponsored_ads"))
  )
  // Box now sizes itself to the ad's own image/video, so nothing gets
  // cropped — the fixed aspect-box below is only a placeholder shown until
  // the real dimensions are known, never the final render.
  const [naturalAspect, setNaturalAspect] = useState<number | null>(null)

  useEffect(() => {
    if (canShow) markAdShown("inline_grid")
  }, [canShow])

  // Track impression when ad comes into view
  useEffect(() => {
    // Matches the render gate above: only the upgrade-promo ad is ever
    // skipped for an ad-free viewer. A normal house ad that's correctly
    // rendering to an ad-free viewer still gets its impression tracked —
    // it's real content actually shown, no reason to undercount it.
    //
    // This used to be a `if (!hasConsent) return null` BEFORE this useEffect,
    // which is a Rules-of-Hooks violation — React detected a "change in the
    // order of Hooks" and crashed the whole grid whenever hasConsent or
    // isPremiumUser resolved to a different value than on first render
    // (e.g. premium status arriving async after mount). All early returns
    // now live after every hook, same as the isPremiumUser/canShow checks
    // below already did.
    if ((ad.is_upgrade_promo && (!hasConsent || effectivePremiumUser)) || hasTrackedImpression || !adRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasTrackedImpression) {
          trackImpression({
            campaign_id: ad.campaign_id,
            // House ads (ad.type "internal") carry a text id — passed so the
            // impression attributes to house_ad_id rather than being dropped
            // by the uuid guard on the route. See migration 20261114000000.
            house_ad_id: ad.type === "internal" ? ad.id : undefined,
            slot_id: "grid_inline",
            session_id: sessionId,
            page_url: typeof window !== "undefined" ? window.location.href : "",
            device_type: typeof window !== "undefined" && window.innerWidth < 768 ? "mobile" : "desktop",
            viewable: true,
            view_duration_ms: 0,
          })
          setHasTrackedImpression(true)
        }
      },
      { threshold: 0.5 },
    )

    observer.observe(adRef.current)
    return () => observer.disconnect()
  }, [ad.campaign_id, sessionId, hasTrackedImpression, effectivePremiumUser, hasConsent])

  // Only the membership-upsell house ad (is_upgrade_promo) is suppressed for
  // an ad-free viewer — a real premium member, or a free user mid Skip-Ads
  // grant (that's what !hasConsent means here, see components/cookie-
  // consent.tsx). Every other house ad and every creator promo (which never
  // sets is_upgrade_promo at all) shows to an ad-free viewer same as anyone
  // else — pitching "/subscribe" to someone who already has ad-free status
  // is nonsensical, not just redundant, but general self-promo content and
  // creator content are "part of the app itself," not something to hide.
  if (ad.is_upgrade_promo && (!hasConsent || effectivePremiumUser)) return null
  if (!canShow) return null

  const handleClick = () => {
    trackClick(ad.id, ad.campaign_id, ad.type === "internal" ? ad.id : undefined)
    const isInternal = ad.click_url.startsWith("/")
    if (isInternal) {
      router.push(ad.click_url)
    } else {
      window.open(ad.click_url, "_blank", "noopener,noreferrer")
    }
  }

  // Fallback box shape used only until the real media loads and reports its
  // natural aspect ratio (see naturalAspect above) — at that point the box
  // switches to fit the image/video exactly, so nothing is ever cropped.
  const aspectRatioClass = { square: "aspect-square", portrait: "aspect-[3/4]", landscape: "aspect-video" }[aspectRatio]

  const showBadge = !ad.hide_badge
  // The dim gradient exists to keep bottom-anchored text (title/subtitle/
  // cta/promo) readable — it has nothing to do with the badge, which sits
  // top-left. It used to key off `showBadge` too, so any ad with its badge
  // on got the bottom-fade regardless of whether there was bottom text to
  // protect — reported live: it was washing out creatives whose headline is
  // baked directly into the artwork (no title/subtitle set at all), for zero
  // readability benefit. hide_dim is an explicit admin override on top,
  // for a real title/subtitle ad that wants the dim off anyway.
  const hasBottomText = !!ad.title || !!ad.subtitle || !!ad.cta_text || !!ad.promo_code
  const showGradient = hasBottomText && !ad.hide_dim

  const getIcon = () => {
    switch (ad.type) {
      case "internal":
        return <Crown className="h-3 w-3" />
      case "promo":
        return <Gift className="h-3 w-3" />
      default:
        return <Sparkles className="h-3 w-3" />
    }
  }

  return (
    <div
      ref={adRef}
      className={cn(
        "relative overflow-hidden cursor-pointer group",
        "rounded-lg border border-border/50",
        "transition-all duration-300",
        isHovered && "scale-[1.02] shadow-lg shadow-primary/20 border-primary/50",
        className,
      )}
      style={{
        background: ad.background_gradient || "linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--muted)) 100%)",
      }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Ad Media — image, gif, or video. The box fits the media's own
          aspect ratio (naturalAspect) once known, so nothing is cropped;
          aspectRatioClass is only the placeholder shape before that. */}
      <div
        className={cn("relative w-full overflow-hidden", (naturalAspect == null || fixedShape) && aspectRatioClass)}
        style={naturalAspect != null && !fixedShape ? { aspectRatio: naturalAspect } : undefined}
      >
        {ad.media_type === "video" ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            src={ad.media_url || ad.image_url}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-contain transition-transform duration-500 group-hover:scale-110"
            onLoadedMetadata={(e) => {
              const v = e.currentTarget
              if (v.videoWidth && v.videoHeight) setNaturalAspect(v.videoWidth / v.videoHeight)
            }}
            onError={(e) => { (e.currentTarget as HTMLVideoElement).style.display = "none" }}
          />
        ) : ad.media_type === "gif" ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={ad.media_url || ad.image_url}
            alt={ad.title}
            className="absolute inset-0 w-full h-full object-contain transition-transform duration-500 group-hover:scale-110"
            onLoad={(e) => {
              const img = e.currentTarget
              if (img.naturalWidth && img.naturalHeight) setNaturalAspect(img.naturalWidth / img.naturalHeight)
            }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none" }}
          />
        ) : (
          <Image
            src={ad.image_url || "/placeholder.svg"}
            alt={ad.title}
            fill
            className="object-contain transition-transform duration-500 group-hover:scale-110"
            sizes="(max-width: 768px) 50vw, 33vw"
            onLoad={(e) => {
              const img = e.currentTarget as HTMLImageElement
              if (img.naturalWidth && img.naturalHeight) setNaturalAspect(img.naturalWidth / img.naturalHeight)
            }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none" }}
          />
        )}

        {/* Gradient Overlay — only when there's bottom text to protect, and
            never when the ad has explicitly turned it off */}
        {showGradient && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        )}
      </div>

      {/* Sponsored Badge */}
      {showBadge && (
        <div className="absolute top-2 left-2 flex items-center gap-1">
          <Badge
            variant="secondary"
            className={cn(
              "text-[10px] px-1.5 py-0.5 backdrop-blur-sm",
              ad.type === "internal" && "bg-primary/90 text-primary-foreground",
              ad.type === "promo" && "bg-green-500/90 text-white",
              ad.type === "external" && "bg-muted/90",
            )}
          >
            {getIcon()}
            <span className="ml-1">
              {ad.badge_text ?? (ad.type === "internal" ? "Upgrade" : ad.type === "promo" ? "Deal" : "Sponsored")}
            </span>
          </Badge>
        </div>
      )}


      {/* Dismiss Button (optional) */}
      {showDismiss && onDismiss && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-6 w-6 bg-black/50 hover:bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation()
            onDismiss()
          }}
        >
          <X className="h-3 w-3" />
        </Button>
      )}

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-3 space-y-1">
        {/* Promo Code */}
        {ad.promo_code && (
          <div className="flex items-center gap-1.5 mb-1">
            <Percent className="h-3 w-3 text-green-400" />
            <span className="text-xs font-mono bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">
              {ad.promo_code}
            </span>
            {ad.discount_text && <span className="text-xs text-green-400 font-medium">{ad.discount_text}</span>}
          </div>
        )}

        {/* Title — omitted entirely for creatives that already bake their own
            headline into the image (see house_ads seed data), so there's no
            empty <h4> taking up space or a redundant "Learn More" fallback
            floating over art that already has its own CTA drawn in. */}
        {ad.title && <h4 className="text-sm font-semibold text-white leading-tight line-clamp-2">{ad.title}</h4>}

        {/* Subtitle */}
        {ad.subtitle && <p className="text-xs text-white/70 line-clamp-1">{ad.subtitle}</p>}

        {/* CTA */}
        {ad.cta_text && (
          <div
            className={cn(
              "flex items-center gap-1.5 text-xs font-medium text-primary",
              "opacity-0 group-hover:opacity-100 transition-opacity pt-1",
            )}
          >
            <span>{ad.cta_text}</span>
            <ExternalLink className="h-3 w-3" />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Wraps InlineGridAd to optionally rotate through `pool` while the slot is
 * on screen, instead of locking to the one `initialAd` it was assigned at
 * injection time. `cycleSeconds` <= 0 (the default) skips the timer
 * entirely and just renders `initialAd` — identical to the pre-rotation
 * behavior, so static grids pay zero cost for this existing.
 */
export function RotatingInlineGridAd({
  initialAd,
  pool,
  cycleSeconds,
  aspectRatio,
  fixedShape,
  onDismiss,
}: {
  initialAd: InlineAd
  pool: InlineAd[]
  cycleSeconds: number
  aspectRatio?: "square" | "portrait" | "landscape"
  fixedShape?: boolean
  onDismiss?: () => void
}) {
  const [current, setCurrent] = useState(initialAd)

  useEffect(() => {
    if (cycleSeconds <= 0 || pool.length <= 1) return
    const interval = setInterval(() => {
      setCurrent((prev) => {
        const idx = pool.findIndex((a) => a.id === prev.id)
        return pool[(idx + 1) % pool.length]
      })
    }, cycleSeconds * 1000)
    return () => clearInterval(interval)
  }, [cycleSeconds, pool])

  return <InlineGridAd ad={current} aspectRatio={aspectRatio} fixedShape={fixedShape} onDismiss={onDismiss} />
}

// Helper to inject ads into a posts array
// Supports image ads, video ads, and network display ads
export type GridAdSlot =
  // `pool` is the full ad list this slot was chosen from — unused for a
  // static slot (the default), but lets <RotatingInlineGridAd> below rotate
  // through it when cycle_seconds is configured, without threading the
  // whole ads array separately through booru-grid.tsx's render loop.
  | { type: "ad"; ad: InlineAd; pool: InlineAd[] }
  | { type: "video_ad"; ad: import("./InFeedVideoAd").InFeedVideoAdData }
  | { type: "network_ad"; embedJs: string; label?: string }

/**
 * Album/gallery placement — bookended instead of cadenced.
 *
 * One ad before the first page, and a closing one after the last page only
 * once the album is at least `threshold` pages long. Below the threshold an
 * album gets exactly one ad total. Nothing is ever inserted between pages:
 * an opened album is a single continuous work being read straight through,
 * where the browse grid's every-N rhythm reads as interruption rather than
 * inventory.
 */
export function injectBookendAds<T>(
  posts: T[],
  ads: InlineAd[],
  threshold = 10,
): (T | GridAdSlot)[] {
  if (!ads.length || posts.length === 0) return posts as (T | GridAdSlot)[]

  const result: (T | GridAdSlot)[] = [{ type: "ad", ad: ads[0], pool: ads }, ...posts]
  if (posts.length >= threshold) {
    // Prefer a different creative for the closing slot so a long album isn't
    // topped and tailed by the identical ad; falls back to the same one when
    // only a single ad is available.
    result.push({ type: "ad", ad: ads[1 % ads.length], pool: ads })
  }
  return result
}

/**
 * `maxSlots` is a hard ceiling on the total number of ad slots inserted,
 * independent of `frequency`'s spacing. Without it, a long infinite-scroll
 * grid got a new slot every `frequency` posts for the ENTIRE list — reported
 * live as house ads feeling "spammed" once a session scrolled past a couple
 * hundred posts. Slots still land at the same `frequency`-spaced positions
 * (so the first ones you see are unchanged); injection just stops once the
 * cap is hit instead of continuing for the rest of the list.
 */
export function injectAdsIntoPosts<T>(
  posts: T[],
  ads: InlineAd[],
  frequency = 12,
  maxSlots = Infinity,
): (T | GridAdSlot)[] {
  if (!ads.length || frequency <= 0 || maxSlots <= 0) return posts as (T | GridAdSlot)[]

  const result: (T | GridAdSlot)[] = []
  let adIndex = 0
  let slotsInserted = 0

  posts.forEach((post, index) => {
    result.push(post)
    if (slotsInserted < maxSlots && (index + 1) % frequency === 0 && adIndex < ads.length) {
      result.push({ type: "ad", ad: ads[adIndex % ads.length], pool: ads })
      adIndex++
      slotsInserted++
    }
  })

  return result
}
